import { get, compact } from 'lodash'
import { HTMLElementModel } from 'Pages/knowledge'
import { KNOWLEDGE_TABLE_OF_CONTENT_DEPTH, NEWS_LENGTH } from 'Src/constants/Global'

enum ElementTypes {
	CONTENT = 'content',
}
let contentLength = 0
let sliced = false

const clearedSymbols = ['&nbsp;']

const hasDownloadAttribute = (html: string, urls: string[], index: number): boolean => {
	const i = html.indexOf(urls[index])
	return html.slice(i + urls[index].length, i + urls[index].length + 10).includes('download')
}

const removeBraces = (html: string): string => html.replace(new RegExp('[<>]', 'gi'), '')

const isHtmlTag = (html: string): boolean => !!html.match(new RegExp('[<>]', 'g'))

const isCloseHtmlTag = (html: string): boolean =>
	!!html.match(new RegExp('</', 'g')) ||
	!!html.match(new RegExp('/>', 'g')) ||
	!!html.match(new RegExp('<br>', 'g'))

const isOwnClosedTag = (html: string): boolean => !!html.match(new RegExp('<img.*>', 'g'))

const wordSlice = (text: string, length: number): string => {
	const slicedText = text.slice(0, length).split(' ')
	slicedText.pop()
	slicedText.push('...')
	return slicedText.join(' ')
}

export const parseHtmlTags = (html: string): any => {
	const tagsTree: HTMLElementModel | string = new HTMLElementModel('div')
	let currentElement = tagsTree
	/* eslint-disable-next-line */
	const tags = compact(
		html.match(new RegExp(`(<[^<>]+>|[-?0-9a-zA-Zа-яёА-ЯЁ _/*'"@;№#$%&.,!?:…()+=]+)`, 'g'))
	).filter((item: string): boolean => item !== ' ')
	if (!tags) {
		return null
	}
	tags.forEach((tag: string): void => {
		if (isHtmlTag(tag)) {
			if (!isCloseHtmlTag(tag) || isOwnClosedTag(tag)) {
				const tagWithAttributes = removeBraces(tag).split(' ')
				const [t, ...attributes] = tagWithAttributes
				const newElement = new HTMLElementModel(removeBraces(t))
				currentElement.setChildren(newElement)
				newElement.setParent(currentElement)
				newElement.setAttributes(attributes)
				if (!isCloseHtmlTag(tag)) {
					currentElement = newElement
				}
				if (isOwnClosedTag(tag)) {
					currentElement = currentElement.parent ? currentElement.parent : currentElement
				}
			} else {
				currentElement = currentElement.parent ? currentElement.parent : currentElement
			}
		} else {
			const newElement = new HTMLElementModel(ElementTypes.CONTENT)
			newElement.setContent(tag)
			newElement.setParent(currentElement)
			currentElement.setChildren(newElement)
		}
	})
	return tagsTree
}

export const concatHtmlTags = (element: HTMLElementModel, length: number = NEWS_LENGTH): string => {
	if (element.tag === ElementTypes.CONTENT) {
		contentLength += element.content.length
		if (length && contentLength > length) {
			if (!sliced) {
				sliced = true
				return wordSlice(element.content, length)
			}
			return
		}
		return element.content
	}
	if (element.childrens && element.childrens.length) {
		const html = element.childrens.map((el: HTMLElementModel): string => {
			if (el.tag === ElementTypes.CONTENT) {
				return concatHtmlTags(el, length)
			}
			if (length && contentLength > length) {
				return
			}
			return `<${el.tag} ${el.attributes ? el.attributes.join(' ') : ''}>${concatHtmlTags(
				el,
				length
			)}</${el.tag}>`
		})

		return html.join('')
	}
	return ''
}

const getHeaderNumber = (tag: string): number => +tag.split('')[1]

const isHeaderTag = (tag: string): boolean =>
	tag.split('')[0] === 'h' && typeof +tag.split('')[1] === 'number'

const getHashedId = (): string => `${Math.random() + Date.now() + Math.random()}`

const clearSymbols = (text: string): string => {
	let parsedText = text
	clearedSymbols.forEach((symbol: string): void => {
		parsedText = parsedText.replace(symbol, '')
	})
	return parsedText
}

const getInnerText = (child: HTMLElementModel): string => {
	let title = ''
	if (child.childrens && child.childrens.length) {
		child.childrens.forEach((ch: HTMLElementModel): void => {
			if (ch.childrens && ch.childrens.length) {
				title += getInnerText(ch)
			}
			if (ch.tag && ch.tag.toLowerCase() === 'content' && ch.content) {
				title += clearSymbols(ch.content)
			}
		})
	}
	return title
}

const getContent = (element): any => {
	const title = getInnerText(element)
	const url = getHashedId()
	const content = {
		url: `#${url}`,
		title,
		childrens: [],
	}
	return { url, content }
}

export const replaceUrl = ({
	html,
	urls,
	index,
	url,
}: {
	html: string
	urls: string[]
	index: number
	url: string
}): string => {
	const fileName = urls[index]
	const shortFileName = fileName.split('/').pop()
	const i = html.indexOf(urls[index])
	if (i + 1) {
		if (hasDownloadAttribute(html, urls, index)) {
			return html.replace(urls[index], url)
		}
		return html.replace(urls[index], `${url}" download="${shortFileName}"`)
	}
	return html
}

export const sliceHtmlInnerText = (html: string, length: number): string => {
	const parsedElements = parseHtmlTags(html)
	const returnHtml = concatHtmlTags(parsedElements, length)
	contentLength = 0
	sliced = false
	return returnHtml
}

export const getTableContentTree = (
	html: string,
	depth: number = KNOWLEDGE_TABLE_OF_CONTENT_DEPTH
): { article: string; table_of_content: any } => {
	const tree = parseHtmlTags(html)
	let article = ''
	const table_of_content = []
	let currentTag = null
	const children = tree.childrens.length === 1 ? tree.childrens[0].childrens : tree.childrens
	if (tree && tree.childrens && tree.childrens.length) {
		children.forEach((child): any => {
			if (!child.childrens || !child.childrens.length) return
			if (isHeaderTag(child.tag) && getHeaderNumber(child.tag) <= depth) {
				if (!currentTag) {
					const { url, content } = getContent(child)
					content.parent = null
					child.attributes.push(`id=${url}`)
					table_of_content.push(content)
					currentTag = { element: child, parent: null, content }
					return currentTag
				}
				if (getHeaderNumber(currentTag.element.tag) < getHeaderNumber(child.tag)) {
					const { url, content } = getContent(child)
					child.attributes.push(`id=${url}`)
					currentTag.content.childrens.push(content)
					content.parent = currentTag
					currentTag = { element: child, parent: { ...currentTag }, content }
					return currentTag
				}
				if (getHeaderNumber(currentTag.element.tag) === getHeaderNumber(child.tag)) {
					const { url, content } = getContent(child)
					child.attributes.push(`id=${url}`)
					content.parent = null
					if (currentTag.parent) {
						content.parent = currentTag.parent
						currentTag.parent.content.childrens.push(content)
					} else {
						table_of_content.push(content)
					}
					currentTag = { element: child, parent: currentTag.parent, content }
					return currentTag
				}
				if (getHeaderNumber(currentTag.element.tag) > getHeaderNumber(child.tag)) {
					const { url, content } = getContent(child)
					child.attributes.push(`id=${url}`)
					content.parent = null
					let element = get(currentTag, 'parent', currentTag)
					let parent = get(element, 'parent', null)
					if (!element || !parent) {
						table_of_content.push(content)
						currentTag = { element: child, parent: null, content }
						return currentTag
					}
					if (getHeaderNumber(element.element.tag) > getHeaderNumber(child.tag)) {
						element = get(element, 'parent', element)
						parent = get(element, 'parent', null)
					}
					if (parent) {
						content.parent = parent
						parent.content.childrens.push(content)
						currentTag = { element: element.element, parent, content }
					} else {
						table_of_content.push(content)
						currentTag = { element: element.element, parent: null, content }
					}
				}
			}
		})
	}
	article = concatHtmlTags(tree, 0)
	contentLength = 0
	return { article, table_of_content }
}

export const fixHtmlError = (html: string) => {
	if (!html) return html
	const firstTag = html.substring(0, 3)
	if (firstTag !== '<p>') html = `<p>${html}</p>`
	const startIframeStr = 'src="<iframe'
	const endIframeStr = '/iframe>"'
	const startPosition = html.indexOf(startIframeStr)
	const endPosition = html.indexOf(endIframeStr, startPosition + startIframeStr.length)
	if (startPosition + 1 && endPosition + 1) {
		const startSrcStr = 'src="'
		const endSrcStr = '"'

		const stringWithError = html.substring(startPosition, endPosition + endIframeStr.length)
		const startSrcPosition = stringWithError.indexOf(startSrcStr, startSrcStr.length)
		const endSrcPosition = stringWithError.indexOf(endSrcStr, startSrcPosition + startSrcStr.length)
		const srcCurrent = stringWithError.substring(
			startSrcPosition,
			endSrcPosition + endSrcStr.length
		)
		const value = html.replace(stringWithError, srcCurrent)
		return fixHtmlError(value)
	}
	return html.replace(/alt="undefined"/g, 'alt=" "')
}
